import React from 'react';
import { IconProvider } from '@/components/ui';

interface MigrationStrategySelectorProps {
  selectedStrategy: 'merge' | 'overwrite';
  onStrategyChange: (strategy: 'merge' | 'overwrite') => void;
}

export const MigrationStrategySelector: React.FC<MigrationStrategySelectorProps> = ({
  selectedStrategy,
  onStrategyChange,
}) => {
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
        Migration Strategy
      </h3>

      <div className="space-y-2">
        {/* Merge Strategy */}
        <div
          className={`border rounded-lg p-4 cursor-pointer transition-all ${
            selectedStrategy === 'merge'
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30 ring-2 ring-blue-200 dark:ring-blue-800'
              : 'border-gray-300 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-700'
          }`}
          onClick={() => onStrategyChange('merge')}
        >
          <div className="flex items-start gap-3">
            <input
              type="radio"
              name="strategy"
              value="merge"
              checked={selectedStrategy === 'merge'}
              onChange={() => onStrategyChange('merge')}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <IconProvider iconstring="fa-code-merge" size={16} color="#3b82f6" />
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  Merge (Recommended)
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Preserves existing data values while updating the structure to match the new
                blueprint. New fields will be added with default values, and removed fields will
                be deleted.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="text-xs px-2 py-1 bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-400 rounded">
                  ✓ Keeps your data
                </span>
                <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400 rounded">
                  ✓ Safe option
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Overwrite Strategy */}
        <div
          className={`border rounded-lg p-4 cursor-pointer transition-all ${
            selectedStrategy === 'overwrite'
              ? 'border-red-500 bg-red-50 dark:bg-red-950/30 ring-2 ring-red-200 dark:ring-red-800'
              : 'border-gray-300 dark:border-gray-600 hover:border-red-300 dark:hover:border-red-700'
          }`}
          onClick={() => onStrategyChange('overwrite')}
        >
          <div className="flex items-start gap-3">
            <input
              type="radio"
              name="strategy"
              value="overwrite"
              checked={selectedStrategy === 'overwrite'}
              onChange={() => onStrategyChange('overwrite')}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <IconProvider iconstring="fa-rotate-right" size={16} color="#ef4444" />
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  Overwrite
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Resets the object to use the new blueprint structure with default values. All
                existing custom data will be lost. Use this only if you want to start fresh.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="text-xs px-2 py-1 bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-400 rounded">
                  ⚠ Loses your data
                </span>
                <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 dark:bg-yellow-950/30 dark:text-yellow-400 rounded">
                  ! Use with caution
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

